import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useVirtualTourDetailQuery } from '@/client/api/listing-management/virtual-tours.query';
import { useResyncVirtualTourMutation } from '@/client/api/listing-management/virtual-tours.mutation';
import { getApiErrorMessage } from '@/client/utils/errors';
import { allowanceText, EligibilityBadges, fmtDate, LinkedListing, OpenInMatterport, StatusBadge } from './shared';
import { MatterportThumb, ModelPreviewDialog } from './ModelPreview';
import { AddonsSection, MediaSection, PlayerSettingChips, PlayerSettingsDialog } from './TourExtras';
import {
  AddressVisibilityDialog,
  ArchiveDialog,
  MoveDialog,
  ReactivateDialog,
  RenameDialog,
  UnlinkDialog,
  VisibilityDialog,
} from './TourActionDialogs';
import { ReasonDialog } from './ReasonDialog';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="space-y-1">
    <span className="text-xs text-muted-foreground">{label}</span>
    <div className="text-sm font-medium break-words">{children}</div>
  </div>
);

export const TourDetailDrawer: React.FC<{ vtid: number | null; onClose: () => void }> = ({ vtid, onClose }) => {
  const navigate = useNavigate();
  const { data: tour, isLoading } = useVirtualTourDetailQuery(vtid);
  const [showPreview, setShowPreview] = useState(false);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [resyncOpen, setResyncOpen] = useState(false);
  const [dialog, setDialog] = useState<string | null>(null);
  const closeDialog = () => setDialog(null);
  const resync = useResyncVirtualTourMutation();

  // A stale preview must not carry over to the next tour opened in the drawer.
  React.useEffect(() => {
    setShowPreview(false);
    setMediaOpen(false);
  }, [vtid]);

  const caps = tour?.capabilities ?? [];
  const canResync = caps.includes('resync');
  const target = { vtid: tour?.vtid ?? vtid };
  const isActive = tour?.status === 'active';
  const isArchived = tour?.status === 'archived';
  const actions: { key: string; label: string; code: string; show: boolean }[] = [
    { key: 'unlink', label: 'Unlink', code: PERMISSIONS.virtualTour.link, show: caps.includes('unlink') && tour?.eid != null },
    { key: 'move', label: 'Move', code: PERMISSIONS.virtualTour.link, show: caps.includes('move') },
    { key: 'archive', label: 'Archive', code: PERMISSIONS.virtualTour.archive, show: caps.includes('archive') && isActive },
    { key: 'reactivate', label: 'Reactivate', code: PERMISSIONS.virtualTour.reactivate, show: caps.includes('reactivate') && isArchived },
    { key: 'visibility', label: 'Visibility', code: PERMISSIONS.virtualTour.publish, show: caps.includes('visibility') },
    { key: 'address', label: 'Address visibility', code: PERMISSIONS.virtualTour.publish, show: caps.includes('address_visibility') },
    { key: 'player', label: 'Player settings', code: PERMISSIONS.virtualTour.edit, show: caps.includes('embed_options') },
    { key: 'rename', label: 'Rename', code: PERMISSIONS.virtualTour.edit, show: caps.includes('details') },
  ];

  const confirmResync = async (reason: string) => {
    if (!vtid) return;
    try {
      await resync.mutateAsync({ vtid, reason });
      toast.success('Re-sync queued.');
      setResyncOpen(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to re-sync this tour.'));
    }
  };

  return (
    <Sheet open={vtid != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex h-full w-[420px] flex-col overflow-y-auto border-l bg-background p-0 shadow-2xl sm:w-[560px]">
        <div className="border-b p-6">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              Tour #{vtid}
              {tour && <StatusBadge status={tour.status} />}
              {tour && <EligibilityBadges planEligible={tour.plan_eligible} source={tour.source} />}
            </SheetTitle>
            <SheetDescription>Virtual tour detail and sync history.</SheetDescription>
          </SheetHeader>
        </div>

        {isLoading || !tour ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="flex-1 space-y-6 p-6">
            <div className="overflow-hidden rounded-xl border bg-muted/30">
              <MatterportThumb
                id={tour.external_id}
                alt={tour.listing_name ?? 'Tour'}
                onClick={tour.external_id ? () => setMediaOpen(true) : undefined}
              />
              {/* Only http(s): share_url can be a seller-supplied link. */}
              {showPreview && tour.share_url && /^https?:\/\//i.test(tour.share_url) ? (
                <iframe
                  src={tour.share_url}
                  title={`Tour #${tour.vtid} preview`}
                  className="aspect-video w-full border-0"
                  allow="fullscreen; xr-spatial-tracking"
                />
              ) : (
                tour.share_url && (
                  <div className="flex aspect-video items-center justify-center bg-muted">
                    <Button variant="outline" size="sm" onClick={() => setShowPreview(true)}>
                      Preview
                    </Button>
                  </div>
                )
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <Field label="Provider">{tour.provider}</Field>
              <Field label="Visibility">{tour.visibility ? <StatusBadge status={tour.visibility} /> : '—'}</Field>
              <Field label="Source">{tour.source}</Field>
              <Field label="Matterport model id">
                {tour.external_id ? <span className="font-mono text-xs">{tour.external_id}</span> : '—'}
              </Field>
              {tour.allowance && (
                <div className="col-span-2">
                  <Field label="Plan allowance">{allowanceText(tour.allowance)}</Field>
                </div>
              )}
              {tour.embed_options && Object.keys(tour.embed_options).length > 0 && (
                <div className="col-span-2">
                  <Field label="Player settings">
                    <PlayerSettingChips options={tour.embed_options} />
                  </Field>
                </div>
              )}
              <Field label="Asset id (asid)">{tour.asid ?? '—'}</Field>
              {tour.provider === 'matterport' && tour.external_id && (
                <div className="col-span-2">
                  <OpenInMatterport id={tour.external_id} />
                </div>
              )}
              <Field label="Last synced">{fmtDate(tour.last_synced_at)}</Field>
              <div className="col-span-2">
                <Field label="Listing">
                  {tour.eid ? (
                    <div className="flex items-center gap-2">
                      <LinkedListing eid={tour.eid} name={tour.listing_name} onOpen={() => navigate(`/admin/listings/${tour.eid}`)} />
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          tour.listing_live ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                        }`}
                      >
                        {tour.listing_live ? 'Live' : 'Not live'}
                      </span>
                    </div>
                  ) : (
                    '—'
                  )}
                </Field>
              </div>
              {tour.share_url && (
                <div className="col-span-2">
                  <Field label="Share URL">
                    <a
                      href={tour.share_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                    >
                      Open <ExternalLink className="h-3 w-3" />
                    </a>
                  </Field>
                </div>
              )}
              {tour.last_error && (
                <div className="col-span-2">
                  <Field label="Last error">
                    <span className="text-red-600">{tour.last_error}</span>
                  </Field>
                </div>
              )}
            </div>

            <Separator />

            {caps.includes('media') && <MediaSection vtid={tour.vtid} />}
            {caps.includes('addons') && <AddonsSection vtid={tour.vtid} />}
            {(caps.includes('media') || caps.includes('addons')) && <Separator />}

            <div>
              <h4 className="mb-3 text-sm font-semibold">Recent events</h4>
              {tour.events?.length ? (
                <ul className="space-y-2">
                  {tour.events.map((ev) => (
                    <li key={ev.id} className="rounded-md border border-gray-100 px-3 py-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{ev.op}</span>
                        <StatusBadge status={ev.outcome} />
                      </div>
                      {ev.message && <p className="mt-1 text-gray-500">{ev.message}</p>}
                      <p className="mt-1 text-gray-400">{fmtDate(ev.created_at)}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500">No events recorded.</p>
              )}
            </div>
          </div>
        )}

        {tour && (
          <div className="flex flex-wrap gap-2 border-t bg-muted/10 p-6">
            {canResync && (
              <PermissionButton code={PERMISSIONS.virtualTour.edit} onClick={() => setResyncOpen(true)}>
                Re-sync
              </PermissionButton>
            )}
            {actions
              .filter((a) => a.show)
              .map((a) => (
                <PermissionButton key={a.key} variant="outline" code={a.code} onClick={() => setDialog(a.key)}>
                  {a.label}
                </PermissionButton>
              ))}
          </div>
        )}
      </SheetContent>

      <ModelPreviewDialog
        open={mediaOpen}
        onClose={() => setMediaOpen(false)}
        id={tour?.external_id ?? ''}
        name={tour?.listing_name ?? null}
      />
      <ReasonDialog
        open={resyncOpen}
        title="Re-sync this tour?"
        description="Queues a fresh sync from the provider for this tour. This is recorded in the admin audit."
        busy={resync.isPending}
        confirmLabel="Re-sync"
        onCancel={() => setResyncOpen(false)}
        onConfirm={confirmResync}
      />
      {vtid != null && (
        <>
          <UnlinkDialog open={dialog === 'unlink'} onClose={closeDialog} vtid={vtid} />
          <MoveDialog open={dialog === 'move'} onClose={closeDialog} vtid={vtid} />
          <ArchiveDialog open={dialog === 'archive'} onClose={closeDialog} target={target} />
          <ReactivateDialog open={dialog === 'reactivate'} onClose={closeDialog} target={target} />
          <VisibilityDialog open={dialog === 'visibility'} onClose={closeDialog} target={target} current={tour?.visibility} />
          <AddressVisibilityDialog open={dialog === 'address'} onClose={closeDialog} vtid={vtid} />
          <PlayerSettingsDialog open={dialog === 'player'} onClose={closeDialog} vtid={vtid} current={tour?.embed_options} />
          <RenameDialog open={dialog === 'rename'} onClose={closeDialog} vtid={vtid} />
        </>
      )}
    </Sheet>
  );
};

export default TourDetailDrawer;
